import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  BookingAttendee,
  BookingAvailabilityResult,
  RoomWindowAvailability,
  BookingCalendarItem,
  BookingRuleViolation,
  BookingWarning,
  GoogleBookerConnection,
  NextSlotSuggestion,
  ResourceBookingStatus,
  RoomSharingSettings,
  RoomSuggestionResult,
} from '@digemp/shared';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';

export function useResources(params?: Record<string, string | number>, opts?: { enabled?: boolean }) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery({
    queryKey: ['resources', orgId, params],
    queryFn: () => api.get('/resources', { params: params ?? {} }),
    enabled: !!orgId && (opts?.enabled ?? true),
  });
}

export function useResource(id: string | undefined) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery({
    queryKey: ['resources', orgId, id],
    queryFn: () => api.get(`/resources/${id}`),
    enabled: !!orgId && !!id,
  });
}

export function useCreateResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post('/resources', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['resources'] }),
  });
}

export function useUpdateResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch(`/resources/${id}`, data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['resources'] });
      qc.invalidateQueries({ queryKey: ['resources', undefined, id] });
    },
  });
}

export function useArchiveResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/resources/${id}`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['resources'] });
      qc.invalidateQueries({ queryKey: ['resources', undefined, id] });
    },
  });
}

/** Partner orgs this org may share rooms with, and which rooms each may book. */
export function useRoomSharing(enabled = true) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery<RoomSharingSettings>({
    queryKey: ['resources', orgId, 'sharing'],
    queryFn: async () => {
      const res = (await api.get('/resources/sharing')) as { data: RoomSharingSettings };
      return res.data;
    },
    enabled: !!orgId && enabled,
  });
}

/** Replace the partner orgs one room is shared with ([] stops sharing it). */
export function useSetRoomShares() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ resourceId, organizationIds }: { resourceId: string; organizationIds: string[] }) =>
      api.put(`/resources/${resourceId}/shares`, { organizationIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['resources'] }),
  });
}

// ── Bookings ────────────────────────────────────────────────────────

/**
 * scope: 'mine' (booked by me — default), 'invited' (someone else booked, I'm an attendee), 'all'.
 * No web caller since My Bookings moved into the calendar; kept for the open
 * product question of listing the booker's rejected / cancelled bookings.
 */
export function useMyBookings(scope: 'mine' | 'invited' | 'all' = 'mine') {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery({
    queryKey: ['bookings', orgId, 'me', scope],
    queryFn: () => api.get('/bookings/me', { params: scope === 'mine' ? {} : { scope } }),
    enabled: !!orgId,
  });
}

export function usePendingBookings(enabled = true) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery({
    queryKey: ['bookings', orgId, 'pending'],
    queryFn: () => api.get('/bookings/pending'),
    enabled: !!orgId && enabled,
  });
}

export function useResourceBookings(resourceId: string | undefined, from?: string, to?: string) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery({
    queryKey: ['bookings', orgId, resourceId, from, to],
    queryFn: () =>
      api.get(`/bookings/resource/${resourceId}`, { params: { from, to } }),
    enabled: !!orgId && !!resourceId,
  });
}

export function useCreateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      resourceId: string;
      startTime: string;
      endTime: string;
      title?: string;
      notes?: string;
      attendees?: BookingAttendee[];
      acknowledgeWarnings?: boolean;
    }) => api.post('/bookings', data) as Promise<{ data: { status: ResourceBookingStatus } }>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookings'] }),
  });
}

export function useUpdateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        resourceId?: string;
        startTime?: string;
        endTime?: string;
        title?: string | null;
        notes?: string | null;
        attendees?: BookingAttendee[];
        acknowledgeWarnings?: boolean;
      };
    }) => api.patch(`/bookings/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookings'] }),
  });
}

export function useRespondBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { action: 'approve' | 'reject'; reason?: string };
    }) => api.post(`/bookings/${id}/respond`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookings'] }),
  });
}

export function useCancelBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/bookings/${id}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookings'] }),
  });
}

// ── Attendee directory (slim org members: id, name, work email) ─

export interface AttendeeDirectoryUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

/** `enabled: false` for viewers without resource.book (the endpoint requires it). */
export function useAttendeeDirectory(enabled = true) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery<AttendeeDirectoryUser[]>({
    queryKey: ['attendee-directory', orgId],
    queryFn: async () => {
      const res = (await api.get('/bookings/attendee-directory')) as {
        data?: AttendeeDirectoryUser[];
      };
      return res.data ?? [];
    },
    enabled: !!orgId && enabled,
  });
}

/**
 * Whether the viewer approves bookings for any room — set as a room's approver,
 * or holding approve/manage-bookings. Drives the Approvals menu item.
 */
export function useIsBookingApprover(enabled: boolean) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery<boolean>({
    queryKey: ['booking-approver-status', orgId],
    queryFn: async () => {
      const res = (await api.get('/bookings/approver-status')) as { data?: { isApprover?: boolean } };
      return res.data?.isApprover ?? false;
    },
    enabled: enabled && !!orgId,
  });
}

/**
 * Server booking rules the calendar mirrors — how many minutes back a one-off
 * booking may start (BOOKING_PAST_ALLOWANCE_MINUTES; 0 = future only).
 */
export function useBookingSettings() {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery<{ pastAllowanceMinutes: number }>({
    queryKey: ['booking-settings', orgId],
    queryFn: async () => {
      const res = (await api.get('/bookings/settings')) as { data?: { pastAllowanceMinutes?: number } };
      return { pastAllowanceMinutes: res.data?.pastAllowanceMinutes ?? 0 };
    },
    enabled: !!orgId,
    staleTime: Infinity,
  });
}

// ── Google integration status ───────────────────────────────────────

export function useGoogleStatus() {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery({
    queryKey: ['google-integration', orgId],
    queryFn: () => api.get('/integrations/google/status'),
    enabled: !!orgId,
  });
}

export function useConnectGoogle() {
  return useMutation({
    mutationFn: () => api.get('/integrations/google/connect'),
  });
}

/** Workspace mode: the server's service account acts as `calendarAccount` and as each booker. */
export function useConnectGoogleWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (calendarAccount: string) => api.post('/integrations/google/workspace', { calendarAccount }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['google-integration'] }),
  });
}

export function useDisconnectGoogle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/integrations/google/disconnect'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['google-integration'] }),
  });
}

/** Whether people must connect their own Google Calendar to book (one-account orgs only). */
export function useUpdateGoogleSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bookerConnection: GoogleBookerConnection) =>
      api.patch('/integrations/google/settings', { bookerConnection }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['google-integration'] });
      qc.invalidateQueries({ queryKey: ['google-calendar-me'] });
    },
  });
}

/** Reads my own busy times through Workspace delegation — proves the free/busy setup (recruitment plan §6). */
export function useTestGoogleFreeBusy() {
  return useMutation({
    mutationFn: () =>
      api.post('/integrations/google/test-freebusy') as Promise<{ data: { ok: boolean; busyBlocks: number; message: string } }>,
  });
}

// ── My own Google Calendar connection (docs/google-hybrid-calendar-plan.md) ─

export function useMyGoogleCalendar() {
  const orgId = useAuthStore((s) => s.organization?.id);
  const userId = useAuthStore((s) => s.user?.id);
  return useQuery({
    queryKey: ['google-calendar-me', orgId, userId],
    queryFn: () => api.get('/integrations/google/me'),
    enabled: !!orgId && !!userId,
  });
}

/** This page as a router path — without the app's base (/app in production), which the server adds back. */
function currentRouterPath(): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, '');
  const { pathname, search } = window.location;
  const path = base && pathname.startsWith(base) ? pathname.slice(base.length) || '/' : pathname;
  return `${path}${search}`;
}

/** Google's consent page for my calendar; it comes back to `returnTo` (default: this page). */
async function myGoogleConnectUrl(returnTo?: string): Promise<string | undefined> {
  const res = await api.get('/integrations/google/me/connect', { params: { returnTo: returnTo ?? currentRouterPath() } });
  return (res as unknown as { data?: { url?: string } })?.data?.url;
}

/** Sends the browser to Google's consent page; it comes back to `returnTo` (default: this page). */
export function useConnectMyGoogleCalendar() {
  return useMutation({
    mutationFn: (returnTo?: string) => myGoogleConnectUrl(returnTo),
    onSuccess: (url) => {
      if (url) window.location.href = url;
    },
  });
}

const POPUP_POLL_MS = 400;
const POPUP_GIVE_UP_MS = 10 * 60 * 1000;
/** Where Google sends the popup back to (GoogleReturnPage) and the channel it reports on. */
export const GOOGLE_POPUP_RETURN_PATH = '/google-return';
export const GOOGLE_RETURN_CHANNEL = 'digemp-google-connect';

/** A popup window centred over this one (window.open's own default is the top-left corner). */
export function openCentredPopup(name: string, width = 520, height = 680): Window | null {
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));
  return window.open('', name, `popup=yes,width=${width},height=${height},left=${left},top=${top}`);
}

/**
 * Wait for the consent popup's answer. GoogleReturnPage posts it on a
 * BroadcastChannel and closes the popup itself: Google's pages send a
 * Cross-Origin-Opener-Policy that cuts this window off from the popup, after
 * which `popup.closed` reads true while it is still open and we can neither
 * read nor close it. So "closed" only counts once this window has focus back
 * (the person really closed it); otherwise we wait for the message, up to
 * 10 minutes. Null = closed without an answer.
 */
function waitForPopupReturn(popup: Window): Promise<URLSearchParams | null> {
  return new Promise((resolve) => {
    const started = Date.now();
    const channel = new BroadcastChannel(GOOGLE_RETURN_CHANNEL);
    const finish = (back: URLSearchParams | null) => {
      window.clearInterval(timer);
      channel.close();
      resolve(back);
    };
    channel.onmessage = (e: MessageEvent<string>) => finish(new URLSearchParams(e.data));
    const timer = window.setInterval(() => {
      if ((popup.closed && document.hasFocus()) || Date.now() - started > POPUP_GIVE_UP_MS) finish(null);
    }, POPUP_POLL_MS);
  });
}

/**
 * The same consent page in a popup, for the booking and interview forms: the
 * form behind it keeps what was entered (plan §3). `popup` must be opened in
 * the click itself — one opened after the request returns is blocked.
 */
export function useConnectMyGoogleCalendarInPopup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (popup: Window): Promise<URLSearchParams | null> => {
      const url = await myGoogleConnectUrl(GOOGLE_POPUP_RETURN_PATH).catch((err: unknown) => {
        popup.close();
        throw err;
      });
      if (!url) {
        popup.close();
        return null;
      }
      popup.location.href = url;
      return waitForPopupReturn(popup);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['google-calendar-me'] }),
  });
}

export function useDisconnectMyGoogleCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete('/integrations/google/me'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['google-calendar-me'] }),
  });
}

// ── Phase C: availability, calendar, planner, series (qa/spec/resource-booking.md §C) ─

export interface AvailabilityInput {
  resourceId: string;
  startTime: string;
  endTime: string;
  attendees?: BookingAttendee[];
  bookingId?: string;
}

/**
 * Live hard-rule + warning check for the booking panel. A mutation (POST body)
 * rather than a query because it is fired imperatively on every form change,
 * debounced by the caller. Never saves.
 */
export function useCheckAvailability() {
  return useMutation({
    mutationFn: async (data: AvailabilityInput) => {
      const res = (await api.post('/bookings/check-availability', data)) as {
        data: BookingAvailabilityResult;
      };
      return res.data;
    },
  });
}

/**
 * Which active rooms can take [start, end) on hard rules — the booking form
 * greys out the rest. `bookingId` (edit) is not counted as a clash.
 */
export function useRoomAvailability(startTime?: string, endTime?: string, bookingId?: string, enabled = true) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery<RoomWindowAvailability[]>({
    queryKey: ['bookings', orgId, 'room-availability', startTime, endTime, bookingId ?? null],
    queryFn: async () => {
      const res = (await api.post('/bookings/room-availability', { startTime, endTime, bookingId })) as {
        data: RoomWindowAvailability[];
      };
      return res.data;
    },
    enabled: enabled && !!orgId && !!startTime && !!endTime && endTime > startTime,
    staleTime: 15_000,
    // A new window is a new question — never show the previous window's answer.
    placeholderData: undefined,
  });
}

export function useBookingCalendar(from: string | undefined, to: string | undefined, resourceIds: string[]) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery<BookingCalendarItem[]>({
    queryKey: ['bookings', orgId, 'calendar', from, to, [...resourceIds].sort().join(',')],
    queryFn: async () => {
      const res = (await api.get('/bookings/calendar', {
        params: { from, to, ...(resourceIds.length ? { resourceIds: resourceIds.join(',') } : {}) },
      })) as { data: BookingCalendarItem[] };
      return res.data ?? [];
    },
    enabled: !!orgId && !!from && !!to,
  });
}

export function useSuggestRooms() {
  return useMutation({
    mutationFn: async (data: {
      startTime: string;
      endTime: string;
      attendees?: BookingAttendee[];
      amenities?: string[];
      officeLocationId?: string;
    }) => {
      const res = (await api.post('/bookings/suggest-rooms', data)) as { data: RoomSuggestionResult };
      return res.data;
    },
  });
}

/**
 * The next times one room and everyone invited are all free (the planner
 * scoped to a room, spec §C.5.3) — offered on the booking form when the
 * chosen time doesn't work.
 */
export function useNextFreeTimes(
  input: { resourceId: string; startTime?: string; endTime?: string; attendees?: BookingAttendee[]; bookingId?: string },
  enabled: boolean,
) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery<NextSlotSuggestion[]>({
    queryKey: ['bookings', orgId, 'next-free-times', input],
    queryFn: async () => {
      const res = (await api.post('/bookings/suggest-rooms', input)) as { data: RoomSuggestionResult };
      return res.data.nextSlots;
    },
    enabled: enabled && !!orgId && !!input.resourceId && !!input.startTime && !!input.endTime,
    staleTime: 15_000,
    // A new window is a new question — never show the previous window's answer.
    placeholderData: undefined,
  });
}

export function useCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) => api.post(`/bookings/${bookingId}/check-in`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookings'] }),
  });
}

export interface SeriesSpecInput {
  resourceId: string;
  pattern: number;
  weekdays?: number[];
  /** MONTHLY: days of the month (1–31); omitted = the first date's day. */
  monthDays?: number[];
  firstDate: string;
  endDate: string;
  startLocal: string;
  endLocal: string;
  attendees?: BookingAttendee[];
}

export interface OccurrencePreview {
  date: string;
  resourceId: string;
  startTime: string;
  endTime: string;
  violations: BookingRuleViolation[];
  warnings: BookingWarning[];
  skipped: boolean;
}

export interface SeriesPreview {
  occurrences: OccurrencePreview[];
  hardFailures: number;
  warningCount: number;
  maxEndDate: string;
}

export interface OccurrenceResolution {
  date: string;
  action: 'skip' | 'alternate';
  resourceId?: string;
}

export function usePreviewSeries() {
  return useMutation({
    mutationFn: async (data: SeriesSpecInput & { resolutions?: OccurrenceResolution[] }) => {
      const { resolutions: _ignored, ...spec } = data;
      const res = (await api.post('/booking-series/preview', spec)) as { data: SeriesPreview };
      return res.data;
    },
  });
}

export function useCreateSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      data: SeriesSpecInput & {
        title?: string;
        notes?: string;
        resolutions?: OccurrenceResolution[];
        acknowledgeWarnings?: boolean;
      },
    ) => api.post('/booking-series', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
      qc.invalidateQueries({ queryKey: ['booking-series'] });
    },
  });
}

export interface BookingSeriesRow {
  id: string;
  resourceId: string;
  resource?: { id: string; name: string; timezone: string };
  user?: { id: string; firstName: string; lastName: string };
  /** Organiser of a series booked in Google Calendar who has no DigEmp account. */
  organizerEmail?: string | null;
  title: string | null;
  pattern: number;
  weekdays: number[] | null;
  monthDays?: number[] | null;
  startLocal: string;
  endLocal: string;
  firstDate: string;
  endDate: string;
  timezone: string;
  status: number;
  consecutiveNoShows: number;
}

export function useMySeries(enabled = true) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery<BookingSeriesRow[]>({
    queryKey: ['booking-series', orgId, 'me'],
    queryFn: async () => {
      const res = (await api.get('/booking-series/me')) as { data: BookingSeriesRow[] };
      return res.data ?? [];
    },
    enabled: !!orgId && enabled,
  });
}

export interface SeriesHealthRow {
  series: BookingSeriesRow;
  missedInARow: number;
  recentNoShows: number;
  lastOccurrences: number;
  nextBookingId: string | null;
}

/** Recurring bookings with missed check-ins, for the rooms the caller oversees (scoped server-side). */
export function useSeriesHealth(enabled: boolean) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery<SeriesHealthRow[]>({
    queryKey: ['booking-series', orgId, 'health'],
    queryFn: async () => {
      const res = (await api.get('/booking-series/health')) as { data: SeriesHealthRow[] };
      return res.data ?? [];
    },
    enabled: !!orgId && enabled,
  });
}

function useSeriesAction<TVars, TResult = unknown>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
      qc.invalidateQueries({ queryKey: ['booking-series'] });
    },
  });
}

export function useExtendSeries() {
  return useSeriesAction(
    ({ id, endDate, acknowledgeWarnings, resolutions }: {
      id: string;
      endDate: string;
      acknowledgeWarnings?: boolean;
      resolutions?: OccurrenceResolution[];
    }) => api.post(`/booking-series/${id}/extend`, { endDate, acknowledgeWarnings, resolutions }),
  );
}

export function useResumeSeries() {
  return useSeriesAction(async (id: string) => {
    const res = (await api.post(`/booking-series/${id}/resume`)) as {
      data: { created: number; lostDates: string[] };
    };
    return res.data;
  });
}

/** Without `fromBookingId` every upcoming occurrence is cancelled ("all bookings"). */
export function useCancelSeriesFollowing() {
  return useSeriesAction(({ id, fromBookingId }: { id: string; fromBookingId?: string }) =>
    api.post(`/booking-series/${id}/cancel-following`, fromBookingId ? { fromBookingId } : {}),
  );
}

export interface UpdateSeriesInput {
  resourceId?: string;
  /** Wall clock in the (new) room's time zone. */
  startLocal?: string;
  endLocal?: string;
  title?: string;
  notes?: string;
  attendees?: BookingAttendee[];
  acknowledgeWarnings?: boolean;
}

/** "All bookings" — one change applied to every upcoming occurrence (all or nothing). */
export function useUpdateSeries() {
  return useSeriesAction(async ({ id, data }: { id: string; data: UpdateSeriesInput }) => {
    const res = (await api.patch(`/booking-series/${id}`, data)) as { data: { updated: number } };
    return res.data;
  });
}

export function useRespondSeries() {
  return useSeriesAction(
    async ({ id, action, reason }: { id: string; action: 'approve' | 'reject'; reason?: string }) => {
      const res = (await api.post(`/booking-series/${id}/respond`, { action, reason })) as {
        data: { approved: number; rejected: number; failed: Array<{ bookingId: string; message: string }> };
      };
      return res.data;
    },
  );
}

export function useMakeBookable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (officeRoomId: string) => api.post(`/resources/from-office-room/${officeRoomId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['resources'] }),
  });
}

// ── Approval settings (spec §C.11) ──────────────────────────────────

export interface ApprovalSettings {
  approvers: Array<{ userId: string; name: string; resourceIds: string[] }>;
  rooms: Array<{
    id: string;
    name: string;
    location: string | null;
    status: number;
    requiresApproval: boolean;
    approverIds: string[];
  }>;
}

export function useApprovalSettings() {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery<ApprovalSettings>({
    queryKey: ['resource-approvers', orgId],
    queryFn: async () => {
      const res = (await api.get('/resource-approvers')) as { data: ApprovalSettings };
      return res.data;
    },
    enabled: !!orgId,
  });
}

/** Replace everyone who approves one room — the Approval Settings screen works room by room. */
export function useSetRoomApprovers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ resourceId, userIds }: { resourceId: string; userIds: string[] }) =>
      api.put(`/resource-approvers/resources/${resourceId}`, { userIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['resource-approvers'] });
      qc.invalidateQueries({ queryKey: ['resources'] });
    },
  });
}

// ── Office-map rooms behind bookable rooms (spec §C.1.4) ────────────

export interface OfficeRoomOption {
  id: string;
  name: string;
  type: string;
  capacity: number | null;
  floorName: string;
  locationName: string;
  linkedResourceId: string | null;
  linkedResourceName: string | null;
}

export function useOfficeRoomOptions(enabled = true) {
  const orgId = useAuthStore((s) => s.organization?.id);
  return useQuery<OfficeRoomOption[]>({
    queryKey: ['resources', orgId, 'office-rooms'],
    queryFn: async () => {
      const res = (await api.get('/resources/office-rooms')) as { data: OfficeRoomOption[] };
      return res.data ?? [];
    },
    enabled: !!orgId && enabled,
  });
}
